/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import {
  AnimatedAxis,
  AreaSeries,
  Grid,
  Tooltip,
  XYChart,
} from "@visx/xychart";
import dayjs from "dayjs";
import { Text, theme } from "@nordcloud/gnui";
import { BrickLoader, ChartPlaceholder, LoaderWrap } from "~/components";
import {
  generateKpiTimeTicks,
  percentageFormat,
} from "~/components/Charts/utils";
import { dateFormat } from "~/constants";
import { getFirstItem, getLastItem, isEmpty } from "~/tools";
import {
  chartTheme,
  generateSegmentedDataSeries,
  renderTooltip,
} from "./helpers";
import { CostAllocationTimePoint } from "./types";

const FONT_SIZE = 10;
const VALUE_TICKS = 10;
const TICK_LENGTH = 6;
const CHART_MARGIN = { top: 20, right: 10, bottom: 20, left: 35 };
const AREA_FILL = "#bbdefb";

type Props = {
  width: number;
  height: number;
  data: CostAllocationTimePoint[];
  startDate: string;
  endDate: string;
  loading?: boolean;
};

export function CostAllocationChart({
  width,
  height,
  data = [],
  loading = true,
  startDate,
  endDate,
}: Props) {
  const timeTicks = React.useMemo(
    () => generateKpiTimeTicks(startDate, endDate),
    [startDate, endDate]
  );

  const segmentedDataSeries = React.useMemo(() => {
    const rangeData = data.filter((item) =>
      dayjs(item.date).isBetween(startDate, endDate, "day", "[]")
    );
    return generateSegmentedDataSeries({
      start: startDate,
      end: endDate,
      timePoints: rangeData,
    });
  }, [data, startDate, endDate]);

  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 7 1"
      backgroundColor={theme.color.background.ui01}
    >
      <ChartPlaceholder show={isEmpty(data)} width={width} height={height} />
      <XYChart
        theme={chartTheme}
        xScale={{ type: "band" }}
        yScale={{ type: "linear" }}
        height={height}
        width={width}
        margin={CHART_MARGIN}
      >
        <Grid
          key="grid"
          rows
          columns={false}
          numTicks={VALUE_TICKS}
          strokeDasharray="1,3"
          strokeWidth={1}
        />
        <AnimatedAxis
          key="value-axis"
          hideZero
          orientation="left"
          numTicks={VALUE_TICKS}
          tickLength={TICK_LENGTH}
          tickFormat={percentageFormat}
          stroke={theme.color.border.focus}
          tickStroke={theme.color.border.focus}
          animationTrajectory="min"
          tickLabelProps={() => ({
            fill: theme.color.text.text02,
            fontSize: FONT_SIZE,
            fontFamily: theme.fonts.body,
          })}
        />
        <AnimatedAxis
          key="time-axis"
          orientation="bottom"
          tickLength={TICK_LENGTH}
          stroke={theme.color.border.focus}
          strokeWidth={2}
          tickValues={timeTicks}
          tickStroke={theme.color.border.focus}
          // @ts-expect-error TODO: fix
          ticksComponent={() => Text}
          animationTrajectory="min"
          tickFormat={(date: string) =>
            dayjs(date).format(dateFormat.dayMonthShort)
          }
          tickLabelProps={() => ({
            fill: theme.color.text.text02,
            fontSize: FONT_SIZE,
            textAnchor: "middle",
            fontFamily: theme.fonts.body,
          })}
        />
        {segmentedDataSeries.map((area) => (
          <Area
            key={`${getFirstItem(area).date}-${getLastItem(area).date}`}
            area={area}
          />
        ))}
        <Tooltip<CostAllocationTimePoint>
          detectBounds
          showVerticalCrosshair
          snapTooltipToDatumX
          showDatumGlyph
          renderTooltip={({ tooltipData }) => renderTooltip(tooltipData)}
        />
      </XYChart>
    </LoaderWrap>
  );
}

type AreaProps = {
  area: ReturnType<typeof generateSegmentedDataSeries>[number];
};

function Area({ area }: AreaProps) {
  const firstItem = getFirstItem(area);
  const strokeDashArray =
    firstItem.isEstimated || firstItem.isEstimationPadding ? "5,3" : undefined;

  return (
    <AreaSeries
      dataKey={`estimatedValues-${firstItem.date}`}
      data={area}
      fill={AREA_FILL}
      xAccessor={({ date }) => date}
      yAccessor={({ value }) => value}
      opacity="0.4"
      lineProps={{
        stroke: theme.color.text.text01,
        strokeDasharray: strokeDashArray,
      }}
    />
  );
}
